import {
  beenInFlag,
  hourLabel,
  PARK_HOME_IDS,
  PARK_HOMES,
  type ParkHomeDef,
  type ParkHomeId,
} from '../content/parkHomes';
import { onceToday } from '../content/types';
import type { Interactions } from '../systems/Interactions';
import type { Vec2 } from '../world/layout';
import { buildParkHome, PARK_HOME_SPOTS, type ParkHomeRoom } from '../world/parkHomeRooms';
import type { Activities } from './Activities';
import type { Interiors } from './Interiors';
import type { GameServices } from './services';
import type { IconToken } from '../icons/index';

interface Thing {
  id: string;
  at: Vec2;
  radius?: number;
  icon: IconToken;
  prompt: () => string | null;
  action: () => void;
  highlight?: string;
}

/**
 * The neighbors' trailers: walk in while they're up (they're out in the yard and the door's
 * unlocked), poke around, and get hollered out at bedtime.
 */
export class ParkHomes {
  private readonly rooms = new Map<ParkHomeId, ParkHomeRoom>();

  constructor(
    private readonly s: GameServices,
    private readonly interiors: Interiors,
    private readonly act: Activities,
  ) {
    for (const id of PARK_HOME_IDS) {
      const room = buildParkHome(id, s.world.dynamic, s.world.collision);
      this.rooms.set(id, room);
      const out = PARK_HOMES[id].stoop;
      interiors.addPublic(room.room, { x: out.x, z: out.z + 0.3 });
    }
  }

  private room(id: ParkHomeId): ParkHomeRoom {
    return this.rooms.get(id) as ParkHomeRoom;
  }

  private home(def: ParkHomeDef): boolean {
    return !!this.s.npcs.get(def.npc)?.active;
  }

  private enter(def: ParkHomeDef): void {
    const { store, rng } = this.s;
    if (!this.home(def)) {
      store.toast(def.closed, 'info', ':door:');
      return;
    }
    const room = this.room(def.id).room;
    this.interiors.enterPublic(room.slot);
    if (this.interiors.current !== room) return;
    store.setFlag(beenInFlag(def.id), true);
    store.toast(rng.pick(def.welcome), 'info', ':door:');
  }

  /** Bedtime: whoever's still in there gets sent home. */
  update(): void {
    const current = this.interiors.current;
    if (!current) return;
    for (const def of Object.values(PARK_HOMES)) {
      if (this.room(def.id).room !== current || this.home(def)) continue;
      if (!this.act.canAct()) return;
      this.interiors.leave();
      const who = this.s.npcs.get(def.npc)?.def.name ?? 'Somebody';
      this.s.store.toast(`${who} flips the lights off on you. Bedtime. Out.`, 'info', ':moon:');
      return;
    }
  }

  register(interactions: Interactions): void {
    const { npcs } = this.s;
    for (const def of Object.values(PARK_HOMES)) {
      const room = this.room(def.id);
      interactions.add({
        id: `home-door-${def.id}`,
        at: () => def.stoop,
        radius: 1.5,
        priority: 0.6,
        icon: ':door:',
        prompt: () => {
          const npc = npcs.get(def.npc);
          if (!npc) return null;
          return npc.active
            ? `Go inside ${def.place}`
            : `${def.place} (lights out till ${hourLabel(npc.def.hours[0])})`;
        },
        action: () => this.enter(def),
      });
      interactions.add({
        id: `home-exit-${def.id}`,
        at: () => room.room.exit,
        radius: 1.2,
        priority: 0.5,
        icon: ':door:',
        prompt: () => 'Head outside',
        action: () => this.interiors.leave(),
      });
      for (const t of this.things(def.id)) {
        const at = room.room.world(t.at);
        interactions.add({
          id: `home-${def.id}-${t.id}`,
          at: () => at,
          radius: t.radius ?? 1.3,
          icon: t.icon,
          prompt: t.prompt,
          action: t.action,
          highlight: () => room.things[t.highlight ?? t.id] ?? null,
        });
      }
    }
  }

  /** Sit somewhere in the room (room-local), then stand back up a step in front of it. */
  private sit(id: ParkHomeId, seat: Vec2, seconds: number, done: () => void): void {
    const { player } = this.s;
    if (!this.act.canAct()) return;
    const room = this.room(id).room;
    const at = room.world(seat);
    player.teleport(at.x, at.z, 0);
    player.doBusy({
      pose: 'sit',
      seat: 0.5,
      until: this.s.now() + seconds,
      facing: 0,
      onDone: () => {
        const up = room.world({ x: seat.x, z: seat.z + 0.85 });
        player.teleport(up.x, up.z, 0);
        done();
      },
    });
  }

  private stand(seconds: number, done: () => void): void {
    if (!this.act.canAct()) return;
    this.s.player.doBusy({ pose: 'stand', until: this.s.now() + seconds, onDone: done });
  }

  private things(id: ParkHomeId): Thing[] {
    const { store, ui, rng, player } = this.s;
    const room = this.room(id);
    const say = (title: string, art: IconToken, text: string, leave = 'Huh'): void =>
      ui.modal.show({ title, art, text, choices: [], leave });
    switch (id) {
      case 'tammy': {
        const S = PARK_HOME_SPOTS.tammy;
        return [
          {
            id: 'diffuser',
            at: S.diffuser,
            icon: ':herb:',
            prompt: () => (store.doneToday('tammy_whiff') ? null : 'Take a big whiff of the diffuser'),
            action: () =>
              this.stand(2.5, () => {
                store.markToday('tammy_whiff');
                store.apply({ stats: { health: 5, energy: 4 } });
                store.toast(
                  rng.pick([
                    "Lavender, eucalyptus and something Tammy calls 'Freedom.' Your sinuses open. So does your third eye, a little.",
                    "It's called 'Thieves.' Your wallet feels lighter just smelling it. Your head feels great, though.",
                  ]),
                  'good',
                  ':herb:',
                );
              }),
          },
          {
            id: 'board',
            at: S.board,
            icon: ':sparkles:',
            prompt: () => "Read Tammy's vision board",
            action: () =>
              say(
                'The Vision Board',
                ':sparkles:',
                rng.pick([
                  "A yacht, cut out of a magazine. A beach. A woman in a blazer pointing at a graph. Under it all, in glitter glue: 'FINANCIALLY FREE BY 2019.' Someone has crossed out 2019 and written 2026. And crossed that out.",
                  "In the middle, a photo of Tammy's upline, Crystal, on a jet ski. Tammy has drawn herself on the back of the jet ski. In pen. Holding on.",
                  "A picture of a pink Cadillac, a picture of a different pink Cadillac, and a sticky note: 'manifest it, hun.'",
                ]),
                'Manifest it',
              ),
          },
          {
            id: 'stock',
            at: S.stock,
            icon: ':box:',
            prompt: () => "Count Tammy's inventory",
            action: () =>
              this.stand(2, () =>
                store.toast(
                  'You count 1,412 bottles of essential oil. Tammy has sold nine. Two of them were to herself.',
                  'meme',
                  ':box:',
                ),
              ),
          },
        ];
      }
      case 'earl': {
        const S = PARK_HOME_SPOTS.earl;
        return [
          {
            id: 'recliner',
            at: { x: S.recliner.x, z: S.recliner.z + 0.85 },
            radius: 1.2,
            icon: ':recliner:',
            prompt: () => "Kick back in Earl's recliner (NASCAR's on)",
            action: () =>
              this.sit(id, S.recliner, 4, () => {
                store.apply({ stats: { energy: 12 }, minutes: 30 });
                store.toast(
                  rng.pick([
                    'Two hundred laps, all of them left. You fall asleep on lap 40 and wake up on lap 190. You missed nothing.',
                    "The recliner knows Earl's shape. You are not Earl's shape. It holds you anyway, like a hug from a catcher's mitt.",
                    "A wreck on turn four. You and the empty trailer both yell 'OHHH!' Earl yells it from the yard too.",
                  ]),
                  'good',
                  ':checkered-flag:',
                );
              }),
          },
          {
            id: 'fridge',
            at: S.fridge,
            icon: ':beer:',
            prompt: () => (store.doneToday('earl_fridge') ? null : "Grab a cold one from Earl's fridge"),
            action: () => {
              store.markToday('earl_fridge');
              store.give('beer', 1);
              this.s.sfx('canOpen');
              store.toast(
                "Bottom shelf. Earl yells 'BOTTOM SHELF' from the yard. He can't see you. He knows.",
                'good',
                ':beer:',
              );
            },
          },
          {
            id: 'bass',
            at: S.bass,
            icon: ':singing-bass:',
            prompt: () => "Push the button on Earl's singing bass",
            action: () => {
              room.poke('bass');
              this.s.sfx('whistle');
              ui.hud.float(":notes: Don't worry, be happy :notes:", player.headPosition(), '#9dff6a');
            },
          },
        ];
      }
      case 'dale': {
        const S = PARK_HOME_SPOTS.dale;
        return [
          {
            id: 'cushions',
            at: S.cushions,
            icon: ':coin:',
            prompt: () => (store.doneToday('dale_cushions') ? null : "Dig through Dale's couch cushions"),
            action: () =>
              this.stand(2.5, () => {
                store.markToday('dale_cushions');
                const found = rng.int(2, 9);
                const owed = Number(store.flag('dale_owed') ?? 0);
                this.s.sfx('ting');
                if (owed > 0) {
                  store.setFlag('dale_owed', Math.max(0, owed - found));
                  store.apply({ money: found });
                  store.toast(
                    `$${found} in quarters, a Hot Pocket and a bail receipt. You put the money toward what Dale owes you. He'll never notice. He never notices.`,
                    'good',
                    ':coin:',
                  );
                  return;
                }
                store.apply({ money: found });
                store.toast(
                  `$${found} in loose change, three lighters and a business card that just says 'DALE (A GUY).'`,
                  'good',
                  ':coin:',
                );
              }),
          },
          {
            id: 'plans',
            at: S.plans,
            icon: ':chart-up:',
            prompt: () => "Read Dale's business plan",
            action: () =>
              say(
                "Dale's Whiteboard",
                ':chart-up:',
                rng.pick([
                  "Step 1: firewood. Step 2 is three question marks. Step 3 is a drawing of a yacht. Off to the side: 'NO MORE GOATS' underlined twice.",
                  "A flowchart. Every arrow leads to a box that says 'LAWSUIT (THEM).' One arrow leads to 'LAWSUIT (ME).' That one's been erased real hard.",
                  "'IDEAS': Uber but for mowers. Tinder but for trucks. Crocs but for dogs. 'Crocs but for dogs' is circled and has a star.",
                ]),
                'Bold',
              ),
          },
        ];
      }
      case 'jenkins': {
        const S = PARK_HOME_SPOTS.jenkins;
        return [
          {
            id: 'photos',
            at: S.photos,
            radius: 1.4,
            icon: ':picture:',
            prompt: () => "Look at Jenkins' old photos",
            action: () =>
              say(
                'Lucky Acres, 1974',
                ':picture:',
                rng.pick([
                  "Lucky Acres when it was new: six trailers, a dirt road and a sign that said 'LUCKY ACRES — A FAMILY PLACE.' Somebody has since added 'MOSTLY.'",
                  "A young Jenkins with a full head of hair, a Ford pickup and a woman laughing so hard she's blurry. On the back, in pencil: 'Ruth. The day she hit me with the car.'",
                  "The park's softball team, 1979. They're all holding beers. The trophy is also holding a beer. Somebody taped it there.",
                  'Darlene, maybe eight years old, already holding a clipboard.',
                ]),
                'Back then',
              ),
          },
          {
            id: 'clock',
            at: S.clock,
            icon: ':grandfather-clock:',
            prompt: () => (store.doneToday('jenkins_clock') ? null : "Wind Jenkins' grandfather clock"),
            action: () =>
              this.stand(2.5, () => {
                store.markToday('jenkins_clock');
                room.poke('clock');
                this.s.sfx('lever');
                store.apply({ clout: 1, minutes: 5 });
                store.toast(
                  "The pendulum swings for the first time since 1989. From outside, Jenkins: 'Who touched my clock.' It's not a question. He sounds pleased.",
                  'good',
                  ':grandfather-clock:',
                );
              }),
          },
        ];
      }
      case 'skeeter': {
        const S = PARK_HOME_SPOTS.skeeter;
        return [
          {
            id: 'upload',
            at: S.upload,
            icon: ':video-camera:',
            prompt: () => "Watch Skeeter's latest upload",
            action: () => {
              room.poke('upload');
              ui.modal.show({
                title: 'SKEETER SENDS IT',
                art: ':video-camera:',
                text: rng.pick([
                  "'BACKFLIP OFF A TRAMPOLINE INTO A KIDDIE POOL (GONE WRONG) (NOT CLICKBAIT)' — 31 views. It went wrong. It is not clickbait.",
                  "'RATING EVERY GAS STATION BURRITO IN THE TRI-COUNTY' — 12 views. He rates them all 10/10. The last one, 10/10 'but I'm in the hospital.'",
                  "'I FOUND A POSSUM THE SIZE OF A PONTIAC (REAL)' — 58 views. The possum is normal sized. Skeeter is holding the phone very close to it.",
                ]),
                choices: [
                  {
                    label: 'Like and subscribe',
                    hint: '+Clout',
                    requires: onceToday('skeeter_sub', "You're already subscribed. You're one of four now."),
                    run: ({ store: st }) => {
                      st.markToday('skeeter_sub');
                      return {
                        text: "From the yard, faintly: 'BRO. FOUR SUBSCRIBERS. FOUR.' He sounds like he's crying a little.",
                        clout: 1,
                      };
                    },
                  },
                ],
                leave: 'Close the tab',
              });
            },
          },
          {
            id: 'cans',
            at: S.cans,
            icon: ':energy-drink:',
            prompt: () => (store.doneToday('skeeter_can') ? null : "Shotgun one of Skeeter's energy drinks"),
            action: () =>
              this.stand(2, () => {
                store.markToday('skeeter_can');
                this.s.sfx('canOpen');
                store.apply({ stats: { energy: 20, health: -3 } });
                store.toast(
                  'Flavor: BLUE. Your heart does a kickflip. You can hear colors.',
                  'good',
                  ':energy-drink:',
                );
              }),
          },
        ];
      }
      case 'meemaw': {
        const S = PARK_HOME_SPOTS.meemaw;
        return [
          {
            id: 'candy',
            at: S.candy,
            icon: ':butterscotch:',
            prompt: () =>
              store.doneToday('meemaw_candy') ? null : 'Take a butterscotch from the candy dish',
            action: () =>
              this.stand(2, () => {
                store.markToday('meemaw_candy');
                store.apply({ stats: { hunger: 6, health: 2 } });
                store.toast(
                  "It's a butterscotch. It's been in that dish since 1991. It's fused to two others. It's perfect.",
                  'good',
                  ':butterscotch:',
                );
              }),
          },
          {
            id: 'grandkids',
            at: S.grandkids,
            icon: ':baby:',
            prompt: () => 'Count the grandbabies on the wall',
            action: () =>
              say(
                'The Grandbabies',
                ':baby:',
                rng.pick([
                  "You count fourteen frames. Meemaw says there's sixteen grandbabies. Two of the frames are the same kid. One is a stock photo that came with the frame.",
                  'Every grandbaby is in the same pose, on the same fake bookshelf, at the same Walmart. The backgrounds go from 1988 to last spring. The bookshelf never changes.',
                  "One frame is just a picture of a bass. It's labeled 'TREVOR.' You don't ask.",
                ]),
                'Precious',
              ),
          },
          {
            id: 'dolls',
            at: S.dolls,
            icon: ':dolls:',
            prompt: () => 'Look at the porcelain dolls',
            action: () =>
              store.toast(
                rng.pick([
                  'The dolls are looking at you. You look away. When you look back, they are still looking at you. Different ones, though.',
                  "Each doll has a name tag. They're all named Pearl.",
                  "One of the dolls is missing. The empty spot is dusty. Meemaw doesn't dust that spot. Meemaw dusts everything.",
                ]),
                'meme',
                ':dolls:',
              ),
          },
        ];
      }
    }
  }
}
